import {memo} from 'react';

import styled from 'styled-components';

import {getLocalizationService} from '~/shared/services/localisationService';
import {flipOnLTR} from '~/shared/theme/utils';
import AddressAutoComplete from '~/shared/components/CustomAddressAutoComplete';
import {body16Normal} from '~/shared/theme/typography';

import useAddressFinder from './useAddressFinder';

const Root = styled.div`
  display: flex;
  flex: 1 1 auto;
`;

const HouseNumberInput = styled.input`
  width: 56px;
  height: 100%;
  border: 0;
  font-size: ${({isAddAddressMode}) => (isAddAddressMode ? 16 : 18)}px;
  text-align: center;
  color: ${({theme}) => theme.colors.secondary};

  ::placeholder {
    color: ${({theme}) => theme.colors.secondary};
  }

  ${flipOnLTR`
    border-right: 1px solid ${({theme}) => theme.colors.background};
  `}
`;

const ErrorIndicatorMobileAndDropDown = styled.div.attrs({
  name: 'errorIndicator',
  role: 'alert',
})`
  z-index: 3;
  color: ${({theme}) => theme.colors.error};
  border: 1px solid ${({theme}) => theme.colors.error};
  background: ${({theme}) => theme.colors.surface};
  padding: 12px;
  top: calc(100% + 16px);
  ${body16Normal}
  position: absolute;
  ${flipOnLTR`
    left: 16px;
  `}

  &::after {
    content: '';
    z-index: 1;
    width: 0;
    height: 0;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-bottom: 8px solid ${({theme}) => theme.colors.error};
    position: absolute;
    top: -8px;
    ${flipOnLTR`
      left: 4px;
    `}
  }

  &::before {
    content: '';
    z-index: 2;
    width: 0;
    height: 0;
    border-left: 7px solid transparent;
    border-right: 7px solid transparent;
    border-bottom: 7px solid ${({theme}) => theme.colors.surface};
    position: absolute;
    top: -7px;
    ${flipOnLTR`
      left: 5px;
    `}
  }
`;

const AddressFinder = ({
  location,
  placeholder,
  itemHeight,
  isAddAddressMode,
  onChange,
  setDisableSubmit,
  disableSubmit,
  submit: submitForm,
  isMaxMobile,
  disableAutoFocus,
}) => {
  const {t} = getLocalizationService();

  const {
    errorMessage,
    showAddressNumberInput,
    rootRef,
    onPlaceSelected,
    resetAddress,
    getCurrentLocation,
    handleCustomHouseNumberChange,
    addressSelectorRef,
  } = useAddressFinder({
    submitForm,
    location,
    onChange,
    setDisableSubmit,
    disableAutoFocus,
    disableSubmit,
  });

  return (
    <Root ref={rootRef}>
      <AddressAutoComplete
        inputRef={addressSelectorRef}
        onCurrentLocationClick={getCurrentLocation}
        {...{placeholder, onPlaceSelected, isMaxMobile, resetAddress, itemHeight, isAddAddressMode, location}}
      />
      {errorMessage && <ErrorIndicatorMobileAndDropDown>{errorMessage}</ErrorIndicatorMobileAndDropDown>}
      {showAddressNumberInput && (
        <HouseNumberInput
          placeholder={t('house')}
          initialValue=""
          onChange={e => handleCustomHouseNumberChange(e.target.value)}
          maxLength={5}
          isAddAddressMode={isAddAddressMode}
        />
      )}
    </Root>
  );
};

export default memo(AddressFinder);
